import React, { forwardRef, useState, useEffect, useRef, useImperativeHandle } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import InputText from '../Inputs/InputText';
import { useCombinedRef, useIsInitialRender, useDebounce } from '../../hooks';

// eslint-disable-next-line prefer-arrow-callback
const SearchField = forwardRef(function SearchField(
  {
    initialSearchQuery = '',
    placeholder = 'Search',
    size,
    disabled,
    instanceRef,
    autoFocus = false,
    className,
    onChange,
  },
  ref
) {
  const searchInputRef = useRef();
  const isInitialRender = useIsInitialRender();

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const debouncedSearchQuery = useDebounce(searchQuery);

  useEffect(() => {
    if (!isInitialRender) {
      onChange(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  // Autofocus the search input on mount
  useEffect(() => {
    if (autoFocus) {
      searchInputRef.current.focus({ preventScroll: true });
    }
  }, []);

  const handleClear = (focusOnClear = true) => {
    setSearchQuery('');
    if (focusOnClear) searchInputRef.current.focus({ preventScroll: true });
  };

  // Customizes the instance value that is exposed to parent components when using instanceRef
  useImperativeHandle(instanceRef, () => ({
    clearSearch(focusOnClear) {
      handleClear(focusOnClear);
    },
    getSearchQuery: () => searchQuery,
  }));

  return (
    <InputText
      ref={useCombinedRef(ref, searchInputRef)}
      value={searchQuery}
      name="search-field-component"
      size={size}
      disabled={disabled}
      placeholder={placeholder}
      className={cx('search-field-component', className)}
      iconLeft="hicon-search-short"
      iconRight={
        !!searchQuery && <i className="hicon-close cursor-pointer" onClick={() => handleClear()} />
      }
      onChange={setSearchQuery}
    />
  );
});

SearchField.propTypes = {
  initialSearchQuery: T.string,
  size: T.oneOf(['sm', 'lg']),
  disabled: T.bool,
  placeholder: T.string,
  instanceRef: T.object,
  autoFocus: T.bool,
  className: T.string,
  onChange: T.func.isRequired,
};

export default SearchField;
